import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, SlidersHorizontal, X, Upload, Image as ImageIcon } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const empty = { category_id: "", name: "", description: "", price: 0, image_url: "", ingredients: [], available: true, sort_order: 0 };

export default function MenuManager({ restaurantId }) {
  const q = restaurantId ? `?restaurant_id=${restaurantId}` : "";
  const [menu, setMenu] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [itemDialog, setItemDialog] = useState(null);
  const [modItem, setModItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCatImg, setNewCatImg] = useState("");
  const [catUploading, setCatUploading] = useState("");
  const [ingInput, setIngInput] = useState("");

  const addIngredient = () => {
    const v = ingInput.trim();
    if (!v) return;
    const list = itemDialog.data.ingredients || [];
    if (list.includes(v)) { setIngInput(""); return; }
    setItemDialog({ ...itemDialog, data: { ...itemDialog.data, ingredients: [...list, v] } });
    setIngInput("");
  };
  const removeIngredient = (name) => setItemDialog((prev) => ({ ...prev, data: { ...prev.data, ingredients: (prev.data.ingredients || []).filter((x) => x !== name) } }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/uploads/image`, fd);
    return data.url;
  };

  const uploadNewCatImage = async (file) => {
    if (!file) return;
    setCatUploading("new");
    try { setNewCatImg(await uploadFile(file)); toast.success("Imagen subida"); }
    catch (e) { toast.error(apiErr(e)); } finally { setCatUploading(""); }
  };

  const changeCatImage = async (cat, file) => {
    if (!file) return;
    setCatUploading(cat.id);
    try {
      const url = await uploadFile(file);
      await api.put(`/owner/categories/${cat.id}${q}`, { name: cat.name, image_url: url, sort_order: cat.sort_order || 0 });
      toast.success("Foto de categoría actualizada"); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setCatUploading(""); }
  };

  const removeCatImage = async (cat) => {
    try {
      await api.put(`/owner/categories/${cat.id}${q}`, { name: cat.name, image_url: "", sort_order: cat.sort_order || 0 });
      toast.success("Foto quitada"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setItemDialog((prev) => ({ ...prev, data: { ...prev.data, image_url: url } }));
      toast.success("Imagen subida");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const load = () => api.get(`/owner/menu${q}`).then(({ data }) => setMenu(data.categories));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [restaurantId]);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post(`/owner/categories${q}`, { name: newCat, image_url: newCatImg, sort_order: (menu?.length || 0) }); setNewCat(""); setNewCatImg(""); toast.success("Categoría añadida"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delCategory = async (id) => { try { await api.delete(`/owner/categories/${id}${q}`); toast.success("Categoría eliminada"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const saveItem = async () => {
    setSaving(true);
    const d = itemDialog.data;
    const payload = { ...d, price: parseFloat(d.price) || 0 };
    try {
      if (itemDialog.mode === "create") await api.post(`/owner/menu-items${q}`, payload);
      else await api.put(`/owner/menu-items/${d.id}${q}`, payload);
      toast.success("Plato guardado"); setItemDialog(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const delItem = async (id) => { try { await api.delete(`/owner/menu-items/${id}${q}`); toast.success("Plato eliminado"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleAvail = async (it) => { try { await api.put(`/owner/menu-items/${it.id}${q}`, { ...it, category_id: it.category_id, available: !it.available }); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (!menu) return <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-neutral-200 bg-white p-4">
        <div className="flex-1 min-w-[200px] space-y-1">
          <Label className="text-xs">Nueva categoría</Label>
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Ej. Bebidas" data-testid="new-category-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Foto</Label>
          <div className="flex items-center gap-2">
            {newCatImg
              ? <img src={newCatImg} alt="" className="w-10 h-10 rounded-full object-cover border border-neutral-200" />
              : <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-300"><Upload className="w-4 h-4" /></div>}
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-neutral-200 px-3 py-2 text-sm hover:bg-neutral-50" data-testid="new-category-image-upload">
              {catUploading === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={catUploading === "new"} onChange={(e) => uploadNewCatImage(e.target.files?.[0])} />
            </label>
            {newCatImg && <button type="button" onClick={() => setNewCatImg("")} className="text-xs text-neutral-400 hover:text-red-500">Quitar</button>}
          </div>
        </div>
        <Button variant="outline" onClick={addCategory} data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" /> Categoría</Button>
        <Button onClick={() => setItemDialog({ mode: "create", data: { ...empty, category_id: menu[0]?.id || "" } })} disabled={menu.length === 0} data-testid="add-item-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo plato
        </Button>
      </div>

      {menu.length === 0 && (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-white py-12 text-center text-neutral-400 text-sm">
          Este restaurante aún no tiene categorías. Crea la primera categoría para empezar el menú.
        </div>
      )}

      {menu.map((cat) => (
        <div key={cat.id} className="rounded-xl border border-neutral-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              {cat.image_url
                ? <img src={cat.image_url} alt="" className="w-10 h-10 rounded-full object-cover border border-neutral-200" data-testid={`cat-img-${cat.id}`} />
                : <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-300"><ImageIcon className="w-4 h-4" /></div>}
              <h3 className="font-admin font-bold">{cat.name} <span className="text-neutral-400 font-normal">({cat.items.length})</span></h3>
            </div>
            <div className="flex items-center gap-1">
              <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs hover:bg-neutral-50" title="Cambiar foto de categoría" data-testid={`cat-image-upload-${cat.id}`}>
                {catUploading === cat.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Foto
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={catUploading === cat.id} onChange={(e) => changeCatImage(cat, e.target.files?.[0])} />
              </label>
              {cat.image_url && <Button variant="ghost" size="icon" className="h-7 w-7 text-neutral-400 hover:text-red-500" onClick={() => removeCatImage(cat)} title="Quitar foto" data-testid={`cat-image-remove-${cat.id}`}><X className="w-4 h-4" /></Button>}
              <Button variant="ghost" size="icon" className="h-7 w-7 text-neutral-400 hover:text-red-500" onClick={() => delCategory(cat.id)} data-testid={`del-cat-${cat.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="divide-y divide-neutral-50">
            {cat.items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 px-4 py-3" data-testid={`mm-item-${it.id}`}>
                {it.image_url && <img src={it.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{it.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{it.description}</div>
                </div>
                <span className="tabular-nums font-medium text-sm">{money(it.price)}</span>
                <div className="flex items-center gap-1.5">
                  <Switch checked={it.available} onCheckedChange={() => toggleAvail(it)} data-testid={`avail-${it.id}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setModItem(it)} title="Modificadores" data-testid={`mods-${it.id}`}><SlidersHorizontal className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setItemDialog({ mode: "edit", data: { ...it } })} data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-neutral-400 hover:text-red-500" onClick={() => delItem(it.id)} data-testid={`del-item-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {cat.items.length === 0 && <div className="px-4 py-6 text-sm text-neutral-400">Sin platos</div>}
          </div>
        </div>
      ))}

      {/* Item dialog */}
      <Dialog open={!!itemDialog} onOpenChange={(o) => !o && setItemDialog(null)}>
        <DialogContent className="font-adminbody">
          <DialogHeader>
            <DialogTitle className="font-admin">{itemDialog?.mode === "create" ? "Nuevo plato" : "Editar plato"}</DialogTitle>
            <DialogDescription>Define nombre, categoría, precio, foto y descripción del plato.</DialogDescription>
          </DialogHeader>
          {itemDialog && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Categoría</Label>
                <Select value={itemDialog.data.category_id} onValueChange={(v) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, category_id: v } })}>
                  <SelectTrigger data-testid="item-category-select"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>{menu.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nombre</Label>
                <Input value={itemDialog.data.name} onChange={(e) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, name: e.target.value } })} data-testid="item-name-input" /></div>
              <div className="space-y-1.5"><Label>Descripción</Label>
                <Textarea value={itemDialog.data.description} onChange={(e) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, description: e.target.value } })} data-testid="item-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Precio</Label>
                  <Input type="number" step="0.01" value={itemDialog.data.price} onChange={(e) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, price: e.target.value } })} data-testid="item-price-input" /></div>
                <div className="space-y-1.5 flex items-center gap-2 pt-6">
                  <Switch checked={itemDialog.data.available} onCheckedChange={(v) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, available: v } })} data-testid="item-avail-switch" />
                  <Label>Disponible</Label></div>
              </div>
              <div className="space-y-1.5"><Label>Foto del plato</Label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-neutral-200 px-3 py-2 text-sm hover:bg-neutral-50" data-testid="item-image-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir imagen
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={uploading} onChange={(e) => uploadImage(e.target.files?.[0])} />
                  </label>
                  {itemDialog.data.image_url && <button type="button" onClick={() => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, image_url: "" } })} className="text-xs text-neutral-400 hover:text-red-500">Quitar</button>}
                </div>
                <Input value={itemDialog.data.image_url} onChange={(e) => setItemDialog({ ...itemDialog, data: { ...itemDialog.data, image_url: e.target.value } })} placeholder="… o pega una URL (PNG, JPG, WEBP)" data-testid="item-image-input" />
                {itemDialog.data.image_url && <img src={itemDialog.data.image_url} alt="preview" className="mt-2 w-full h-32 object-cover rounded-lg" />}
              </div>
              <div className="space-y-1.5"><Label>Ingredientes <span className="text-neutral-400 font-normal">(incluidos por defecto, el cliente puede quitarlos)</span></Label>
                <div className="flex gap-2">
                  <Input value={ingInput} onChange={(e) => setIngInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIngredient(); } }} placeholder="Ej. Cebolla, Tomate, Queso…" data-testid="ingredient-input" />
                  <Button type="button" variant="outline" size="icon" onClick={addIngredient} data-testid="add-ingredient-btn"><Plus className="w-4 h-4" /></Button>
                </div>
                {(itemDialog.data.ingredients || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid="ingredient-chips">
                    {(itemDialog.data.ingredients || []).map((ing) => (
                      <span key={ing} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 border border-neutral-200 px-2.5 py-1 text-xs" data-testid={`ingredient-chip-${ing}`}>
                        {ing}
                        <button type="button" onClick={() => removeIngredient(ing)} className="text-neutral-400 hover:text-red-500" data-testid={`ingredient-remove-${ing}`}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveItem} disabled={saving} data-testid="save-item-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ModifiersDialog item={modItem} q={q} onClose={() => { setModItem(null); load(); }} />
    </div>
  );
}

function ModifiersDialog({ item, q, onClose }) {
  const [groups, setGroups] = useState([]);
  const [gName, setGName] = useState("");
  const [mName, setMName] = useState({});
  const [mPrice, setMPrice] = useState({});

  useEffect(() => { setGroups(item?.modifier_groups || []); }, [item]);
  const refresh = async () => {
    const { data } = await api.get(`/owner/menu${q}`);
    for (const c of data.categories) for (const it of c.items) if (it.id === item.id) setGroups(it.modifier_groups);
  };

  const addGroup = async () => {
    if (!gName.trim()) return;
    try { await api.post(`/owner/modifier-groups${q}`, { menu_item_id: item.id, name: gName, required: false, min_select: 0, max_select: 3 }); setGName(""); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delGroup = async (id) => { try { await api.delete(`/owner/modifier-groups/${id}${q}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const addMod = async (gid) => {
    if (!mName[gid]?.trim()) return;
    try { await api.post(`/owner/modifier-groups/${gid}/modifiers${q}`, { name: mName[gid], price_delta: parseFloat(mPrice[gid]) || 0 }); setMName({ ...mName, [gid]: "" }); setMPrice({ ...mPrice, [gid]: "" }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delMod = async (id) => { try { await api.delete(`/owner/modifiers/${id}${q}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-adminbody max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-admin">Modificadores · {item?.name}</DialogTitle>
          <DialogDescription>Crea grupos de opciones (ej. Extras) y sus opciones con precio adicional.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Nuevo grupo (ej. Extras)" data-testid="new-group-input" />
            <Button variant="outline" onClick={addGroup} data-testid="add-group-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          {groups.map((g) => (
            <div key={g.id} className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">{g.name}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-neutral-400 hover:text-red-500" onClick={() => delGroup(g.id)} data-testid={`del-group-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-1.5 mb-2">
                {g.modifiers.map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-sm bg-neutral-50 rounded px-2 py-1.5">
                    <span>{m.name} <span className="text-neutral-400">+{money(m.price_delta)}</span></span>
                    <button onClick={() => delMod(m.id)} className="text-neutral-400 hover:text-red-500" data-testid={`del-mod-${m.id}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input className="flex-1" placeholder="Opción" value={mName[g.id] || ""} onChange={(e) => setMName({ ...mName, [g.id]: e.target.value })} data-testid={`mod-name-${g.id}`} />
                <Input className="w-24" type="number" step="0.01" placeholder="+$" value={mPrice[g.id] || ""} onChange={(e) => setMPrice({ ...mPrice, [g.id]: e.target.value })} data-testid={`mod-price-${g.id}`} />
                <Button variant="outline" size="icon" onClick={() => addMod(g.id)} data-testid={`add-mod-${g.id}`}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {groups.length === 0 && <p className="text-sm text-neutral-400">Sin grupos de modificadores.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
